<template>
  <div :class="['scope-filter', viewMode ? '-view-mode': '']">
    <m-spinner
      v-if="loading"
      size="sm"
    />
    <div
      v-else
      class="_filter"
    >
      <div class="_inner">
        <leaf-node
          v-for="(child, index) in userScopeTree.children"
          :key="getKey(child)"
          :class="['_leaf', disabled ? '-disabled':'']"
          :node="child"
          :op="selectedOperator"
          :show-op="index !== 0"
          :can-select-op="index === 1"
          :can-delete="userScopeTree.children.length > 1 || canRemoveAll"
          :props="allowedProps"
          :read-only="disabled"
          :show-static-user-selection="showStaticUserSelection"
          :max-tag-text-length="staticUsersMaxTagTextLength"
          :has-dynamic-dates="hasDynamicDates"
          :multi-model="multiModel"
          @change-operator="changeOperator"
          @change="updateChild(index, $event)"
          @delete="remove(index)"
        />
      </div>
    </div>
    <div
      v-if="!disabled"
      class="_add"
    >
      <m-btn
        class="_btn"
        icon="plus"
        :disabled="disabled"
        light
        hide-border
        @click="addNode"
      >
        {{ $t('userScopeFilter.addFilter') }}
      </m-btn>
    </div>
    <div
      v-if="showEmptyInfo && disabled && userScopeTree.children.length === 0"
      class="_empty"
    >
      {{ $t('userScopeFilter.empty') }}
    </div>
  </div>
</template>

<script>
import LeafNode from '@/components/filter/LeafNode.vue';
import { copy } from 'shared/lib/copy';
import { guid } from 'shared/lib/uuid';
import { isEqual } from 'lodash-es';
import { isValidTree } from '@/lib/filter/scope-tree';
import { propertyType, userScopeOperator, userScopeType } from 'shared/constants.json';

export default {
  name: 'ScopeFilter',
  props: {
    value: {
      type: Object,
      default: () => null,
    },
    props: {
      type: Array,
      default: () => [],
    },
    account: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showEmptyInfo: {
      type: Boolean,
      default: false,
    },
    viewMode: {
      type: Boolean,
      default: false,
    },
    showStaticUserSelection: {
      type: Boolean,
      default: false,
    },
    canRemoveAll: {
      type: Boolean,
      default: false,
    },
    staticUsersMaxTagTextLength: {
      type: Number,
      default: 0,
    },
    initWithFirstProp: {
      type: Boolean,
      default: false,
    },
    hasDynamicDates: {
      type: Boolean,
      default: false,
    },
    multiModel: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'update:value'],
  components: { LeafNode },
  data() {
    return {
      userScopeTree: null,
      selectedOperator: userScopeOperator.and,
    };
  },
  computed: {
    firstProp() {
      if (this.allowedProps.length === 0) {
        return { type: '' };
      }
      return this.allowedProps[0].property;
    },
    allowedProps() {
      const notAllowed = [
        propertyType.text,
        propertyType.url,
        propertyType.richText,
      ];

      return this.props.filter((p) => !notAllowed.includes(p.property.type));
    },
  },
  methods: {
    changeOperator(val) {
      this.selectedOperator = val;
      this.emitUpdate({
        ...this.userScopeTree,
        op: val,
      });
    },
    updateChild(index, val) {
      this.userScopeTree.children.splice(
        index,
        1,
        val,
      );
    },
    init() {
      this.userScopeTree = {
        account: { uid: this.account.uid },
        op: userScopeOperator.and,
        scope: null,
        children: [],
      };
      if (this.initWithFirstProp) {
        this.addNode();
      }
    },
    getKey(node) {
      if (typeof node.uid !== 'undefined') {
        return node.uid;
      }
      if (typeof node.key !== 'undefined') {
        return node.key;
      }
      return 0;
    },
    addNode() {
      this.userScopeTree.children.push(
        {
          key: guid(),
          op: userScopeOperator.and,
          children: [
            {
              op: 'and',
              children: [],
              scope: {
                property: this.firstProp,
                type: userScopeType.property,
                staticUsers: null,
                users: [],
                selectedOptions: [],
                numberRange: null,
                timeRange: null,
                relation: null,
              },
            },
          ],
        },
      );
    },
    remove(index) {
      this.userScopeTree.children.splice(index, 1);
      if (this.userScopeTree.children.length === 0) {
        this.emitUpdate(null);
      }
    },
    emitUpdate(userScopeTree) {
      if (!isValidTree(userScopeTree)) {
        return;
      }
      this.$emit('input', copy(userScopeTree));
      this.$emit('update:value', copy(userScopeTree));
    },
  },
  watch: {
    userScopeTree: {
      handler(val) {
        if (isEqual(val, this.value)) {
          return;
        }
        this.emitUpdate(val);
      },
      deep: true,
    },
    value: {
      handler(val) {
        if (JSON.stringify(this.userScopeTree) === JSON.stringify(val)) {
          return;
        }
        if (this.value === null || Object.keys(this.value).length === 0) {
          this.init();
          return;
        }
        this.userScopeTree = copy(val);
      },
      deep: true,
    },
  },
  created() {
    if (this.value === null || Object.keys(this.value).length === 0) {
      this.init();
      this.emitUpdate(this.userScopeTree);
      return;
    }
    this.userScopeTree = copy(this.value);
    this.selectedOperator = this.userScopeTree.op;
  },
};
</script>

<style lang="scss" type="text/scss">
  .scope-filter {
    ._filter {
      display: flex;

      ._inner {
        width: 100%;
      }

      ._leaf {
        margin-bottom: .8rem;

        &:last-of-type {
          &.-disabled {
            margin-bottom: 0;
          }
        }
      }
    }

    ._empty {
      color: $font-color-tertiary;
    }

    &.-view-mode {
      .ant-select-disabled,
      .ant-input-disabled,
      .ant-input-number-disabled {
        color: inherit;
        cursor: inherit !important;
        background: $white;
        background-color: $white;

        .ant-select-selection,
        .ant-input-number-input,
        .ant-calendar-range-picker-input {
          cursor: inherit !important;
          background: $white;
        }
      }

      .ant-select-selection-selected-value {
        padding-right: 0;
      }

      .ant-select-arrow {
        display: none;
      }

      .leaf-node {
        ._property {
          min-width: 0;
        }
      }
    }
  }
</style>
